import { theme } from 'antd'
import type { ThemeConfig } from 'antd'

// Professional theme colors
const colors = {
  primary: '#1a365d',
  primaryHover: '#2c5282',
  secondary: '#475569',
  success: '#22c55e',
  warning: '#f59e0b',
  error: '#ef4444',
  info: '#3b82f6',
  textPrimary: '#1e293b',
  textSecondary: '#64748b',
  borderLight: '#e2e8f0',
  borderMedium: '#cbd5e0',
  bgSecondary: '#f8fafc',
}

export const lightTheme: ThemeConfig = {
  algorithm: theme.defaultAlgorithm,
  token: {
    colorPrimary: colors.primary,
    colorSuccess: colors.success,
    colorWarning: colors.warning,
    colorError: colors.error,
    colorInfo: colors.info,
    colorText: colors.textPrimary,
    colorTextSecondary: colors.textSecondary,
    colorBorder: colors.borderLight,
    colorBgContainer: '#ffffff',
    colorBgLayout: '#f8fafc',
    
    // Typography
    fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
    fontSize: 14,
    fontSizeHeading1: 32,
    fontSizeHeading2: 24,
    fontSizeHeading3: 20,
    fontSizeHeading4: 16,
    fontSizeHeading5: 14,
    fontWeightStrong: 600,
    
    // Borders and Radius
    borderRadius: 6,
    borderRadiusLG: 8,
    borderRadiusSM: 4,
    
    // Heights
    controlHeight: 40,
    controlHeightLG: 48,
    controlHeightSM: 32,
    
    // Shadows
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    boxShadowSecondary: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  },
  components: {
    Button: {
      primaryShadow: '0 2px 4px rgba(26, 54, 93, 0.1)',
      defaultBorderColor: colors.borderMedium,
      fontWeight: 600,
    },
    Input: {
      hoverBorderColor: colors.borderMedium,
      activeBorderColor: colors.primary,
      activeShadow: `0 0 0 3px rgba(26, 54, 93, 0.1)`,
    },
    Card: {
      headerBg: colors.bgSecondary,
      boxShadowTertiary: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    },
    Table: {
      headerBg: colors.bgSecondary,
      headerColor: colors.textSecondary,
      headerSplitColor: colors.borderLight,
      rowHoverBg: colors.bgSecondary,
    },
    Menu: {
      itemSelectedBg: '#f1f5f9',
      itemSelectedColor: colors.primary,
      horizontalItemSelectedBg: 'transparent',
      horizontalItemSelectedColor: colors.primary,
    },
    Tag: {
      defaultBg: '#f1f5f9',
      defaultColor: colors.textPrimary,
    },
    Badge: {
      textFontWeight: 600,
    },
    Form: {
      labelFontSize: 14,
      labelHeight: 24,
      labelColonMarginInlineStart: 2,
      labelColonMarginInlineEnd: 8,
      itemMarginBottom: 20,
    },
    Modal: {
      headerBg: '#ffffff',
      titleFontSize: 20,
      titleLineHeight: 1.5,
    },
    Alert: {
      defaultPadding: '12px 16px',
    },
    Divider: {
      colorSplit: colors.borderLight,
    },
  },
}

export const darkTheme: ThemeConfig = {
  algorithm: theme.darkAlgorithm,
  token: {
    colorPrimary: '#3b82f6',
    colorSuccess: colors.success,
    colorWarning: colors.warning,
    colorError: colors.error,
    colorInfo: colors.info,
    colorText: '#f1f5f9',
    colorTextSecondary: '#94a3b8',
    colorBorder: '#334155',
    colorBgContainer: '#1e293b',
    colorBgLayout: '#0f172a',
    colorBgElevated: '#1e293b',
    
    // Keep same typography and sizing
    fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
    fontSize: 14,
    fontSizeHeading1: 32,
    fontSizeHeading2: 24,
    fontSizeHeading3: 20,
    fontSizeHeading4: 16,
    fontSizeHeading5: 14,
    fontWeightStrong: 600,
    
    borderRadius: 6,
    borderRadiusLG: 8,
    borderRadiusSM: 4,
    
    controlHeight: 40,
    controlHeightLG: 48,
    controlHeightSM: 32,
    
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
    boxShadowSecondary: '0 4px 6px -1px rgba(0, 0, 0, 0.4)',
  },
  components: {
    Button: {
      primaryShadow: '0 2px 4px rgba(59, 130, 246, 0.2)',
      defaultBorderColor: '#475569',
      fontWeight: 600,
    },
    Input: {
      colorBgContainer: '#334155',  // Lighter dark background for better visibility
      colorBorder: '#475569',
      colorText: '#f1f5f9',
      colorTextPlaceholder: '#94a3b8',
      hoverBorderColor: '#64748b',
      activeBorderColor: '#3b82f6',
      activeShadow: `0 0 0 3px rgba(59, 130, 246, 0.1)`,
    },
    Card: {
      colorBgContainer: '#1e293b',
      colorBorderSecondary: '#334155',
    },
    Table: {
      colorBgContainer: '#1e293b',
      headerBg: '#0f172a',
      rowHoverBg: '#334155',
    },
    Menu: {
      darkSubMenuItemBg: '#0f172a',
      darkItemSelectedBg: '#334155',
    },
    Modal: {
      headerBg: '#1e293b',
      contentBg: '#1e293b',
      footerBg: '#1e293b',
    },
  },
}